import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import { AccessLevel, DspAdvertiser } from "@front/m19-api-client";
import { isOrgEligibleForDspAdvertiserRequest, Marketplaces } from "@front/m19-models";
import {
  AccountMarketplaceService,
  AccountSelectionService,
  DspAdvertiserRequestService,
  OrganizationService,
} from "@front/m19-services";
import { IButtonDirective, ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  LinkDspModalComponent,
  LinkDspModalData,
} from "@m19-board/settings/account-settings/link-dsp/link-dsp.component";
import { map, of, switchMap } from "rxjs";
import { RequestCreationStatusPageComponent } from "@m19-board/dsp/dsp-advertiser-request-page/request-creation-status-page.component";
import { OverlayFeatureListComponent } from "@m19-board/overlay/overlay-feature-list/overlay-feature-list.component";
import { OverlayLayoutComponent } from "@m19-board/overlay/overlay-layout/overlay-layout.component";
import { OverlayRule } from "@m19-board/overlay/overlay-service/overlay-rules";

@Component({
  selector: "app-no-dsp-advertiser-linked-overlay",
  imports: [
    TranslocoDirective,
    IButtonDirective,
    RouterLink,
    RequestCreationStatusPageComponent,
    OverlayLayoutComponent,
    OverlayFeatureListComponent,
  ],
  templateUrl: "./no-dsp-advertiser-linked-overlay.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoDspAdvertiserLinkedOverlayComponent {
  static readonly rule = OverlayRule.DSP_NO_LINKED_ADVERTISER;

  private readonly modalService = inject(ModalService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly organizationService = inject(OrganizationService);
  private readonly dspAdvertiserRequestService = inject(DspAdvertiserRequestService);
  private readonly translocoService = inject(TranslocoService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);
  readonly isAdmin = computed(() => this.accountMarketplace()?.accessLevel === AccessLevel.ADMIN);

  private readonly organization = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.organizationService.find(am.resourceOrganizationId!)),
    ),
  );
  readonly hasEligibleBillingPlan = computed(() => isOrgEligibleForDspAdvertiserRequest(this.organization()));
  readonly canRequestAdvertiser = computed(
    () => this.isAdmin() && this.hasEligibleBillingPlan() && !this.linkableGlobalAdvertiser(),
  );

  private readonly organizationAccountMarketplaces = toSignal(
    this.accountSelectionService.selectedOrganizationAccountMarketplaces$,
  );
  // a Global advertiser serves its whole platform: when one is already linked on another marketplace of this
  // account, it can be linked here as is, without going through the Amazon login of the modal
  readonly linkableGlobalAdvertiser = computed<DspAdvertiser | undefined>(() => {
    const accountMarketplace = this.accountMarketplace();
    if (!accountMarketplace) return undefined;
    const platform = Marketplaces[accountMarketplace.marketplace].platform;
    return (this.organizationAccountMarketplaces() ?? []).find(
      (am) =>
        am.marketplace !== accountMarketplace.marketplace &&
        am.accountId === accountMarketplace.accountId &&
        am.resourceOrganizationId === accountMarketplace.resourceOrganizationId &&
        am.dspAdvertiser?.isRegional &&
        am.dspAdvertiser.platform === platform,
    )?.dspAdvertiser;
  });

  private readonly isAdmin$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    map((accountSelection) => accountSelection.accessLevel === AccessLevel.ADMIN),
  );

  protected readonly dspAdvertiserRequest = toSignal(
    this.isAdmin$.pipe(
      switchMap((isAdmin) => {
        if (isAdmin) {
          return this.dspAdvertiserRequestService.dspAdvertiserRequest$;
        }
        return of(undefined);
      }),
    ),
  );

  readonly isLinkingGlobalAdvertiser = signal(false);

  linkDspAdvertiser() {
    this.modalService
      .openModal<LinkDspModalData, DspAdvertiser | undefined>(LinkDspModalComponent, {
        modalTitle: this.translocoService.translate("link-dsp.link_your_dsp_advertiser"),
        data: {
          accountMarketplace: this.accountMarketplace()!,
        },
      })
      .afterClosed()
      .subscribe((dspAdvertiser) => {
        if (dspAdvertiser) this.refreshAfterAdvertiserLinked();
      });
  }

  linkGlobalAdvertiser() {
    const accountMarketplace = this.accountMarketplace();
    const dspAdvertiser = this.linkableGlobalAdvertiser();
    if (!accountMarketplace || !dspAdvertiser) return;

    this.isLinkingGlobalAdvertiser.set(true);
    this.accountMarketplaceService
      .linkDspAdvertiser([accountMarketplace], dspAdvertiser.dspAdvertiserId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLinkingGlobalAdvertiser.set(false);
          this.toasterService.success(this.translocoService.translate("link-dsp.dsp_advertiser_linked_successfully"));
          this.refreshAfterAdvertiserLinked();
        },
        error: (error) => {
          this.isLinkingGlobalAdvertiser.set(false);
          this.toasterService.error(error, this.translocoService.translate("link-dsp.error_linking_dsp_advertiser"));
        },
      });
  }

  private refreshAfterAdvertiserLinked() {
    window.location.reload();
  }
}
